import React, { useState, useMemo, useCallback } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CancelIcon from '@mui/icons-material/Cancel'
import RemoveIcon from '@mui/icons-material/Remove'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { chipVisualState, classifyChipValue } from './BatchChipGroup.visual'
import { resolveChipGesture } from './search-quesionnaries.chip-value'

/**
 * BatchChipGroup - Multi-select chip group for batch questionnaire entry
 *
 * Instead of individual 1-5 scales for each questionnaire in a batch_group,
 * renders clickable chips. Clicked = confirmed (scale 5 / Yes),
 * unclicked = denied (scale 1 / No).
 *
 * Chip clicks update local state and call onChipToggle for each change,
 * which feeds into the parent's pendingAnswers. The bottom "Save" /
 * "Save & Confirm" buttons handle the actual backend save.
 */

interface BatchItem {
  id: string
  questionnaire_id: number
  name: string
  display_name: string
  option_label: string
  first_response?: string | number | null
  first_component_type?: string
  is_recommended?: boolean
}

interface BatchChipGroupProps {
  batchGroup: string
  groupLabel: string
  items: BatchItem[]
  onChipToggle: (item: BatchItem, isSelected: boolean) => void
  onDenyAll?: (items: BatchItem[]) => void
  onChipEdit?: (item: BatchItem) => void
  editingItemName?: string | null
  disabled?: boolean
  uiCategory?: string
}

// Batch groups in these ui_categories ask "does X help / hurt" — the
// answer is exposure-dependent (patient has to have tried X). Cascading
// "No" across all options writes Likert 1 for every option, including
// the ones the patient never tried, which then applies LR− against the
// candidate diagnoses. The same harm the consumer-side fix closed via
// the LLM matcher's not_applicable class (CAPA 1, SOLUTION_LOG 2026-05-24).
// Hide the bulk-deny chip for these categories so the provider marks
// individual options explicitly — unanswered options route through the
// predicate evaluator's return-None path and contribute no LR.
const EXPOSURE_DEPENDENT_CATEGORIES = new Set(['ALLEVIATING', 'AGGRAVATING'])

const BatchChipGroup: React.FC<BatchChipGroupProps> = ({
  batchGroup,
  groupLabel,
  items,
  onChipToggle,
  onDenyAll,
  onChipEdit,
  editingItemName,
  disabled = false,
  uiCategory,
}) => {
  const isExposureDependent = !!uiCategory && EXPOSURE_DEPENDENT_CATEGORIES.has(uiCategory)

  // ADR D1 (QUICK_ENTRY_CHIP_STATE_MACHINE): `first_response` is the SINGLE source of truth for
  // BOTH color AND click-routing — there is no local `selected` set to drift out of sync (the old
  // dual-source bug: a chip could route as "selected" while rendering grey). "Positive/answered"
  // is derived on the fly from the value via classifyChipValue (the same classifier that colors it).
  // The group "All No" label is likewise derived from the items each render.
  const computeAllDenied = (its: BatchItem[]): boolean => {
    const relevant = its.filter(i => i.is_recommended !== false)
    if (relevant.length === 0) return false
    return relevant.every(item => classifyChipValue(item.first_response) === 'no')
  }
  const allDenied = computeAllDenied(items)

  const handleDenyAll = useCallback(() => {
    if (onDenyAll) onDenyAll(items)
  }, [items, onDenyAll])

  // Click on chip body — routed through the shared resolveChipGesture decider (ADR §3.3):
  //  • unanswered / neutral ('3'/'Unknown') → CONFIRM to Yes/5   (onChipToggle true)
  //  • answered (Yes OR No) → OPEN the inline editor              (onChipEdit) — never a silent flip
  const handleChipBodyClick = useCallback((item: BatchItem) => {
    if (disabled) return
    const { action } = resolveChipGesture(item.first_response, item, 'click')
    if (action === 'edit') onChipEdit?.(item)
    else onChipToggle(item, true)
  }, [disabled, onChipEdit, onChipToggle])

  // Click the ✓ icon of a POSITIVE chip → deselect it to Unanswered (ADR D5). On any other value
  // the icon is not a ✓, so let the click bubble to the body (which confirms it).
  const handleIconClick = useCallback((e: React.MouseEvent, item: BatchItem) => {
    if (disabled) return
    if (classifyChipValue(item.first_response) !== 'yes') return
    e.stopPropagation()
    onChipToggle(item, false)
  }, [disabled, onChipToggle])

  // Split items: recommended or answered (bright) first, unanswered non-recommended collapsed
  // An item counts as "answered" if it has a confirmed response (4/5/Yes), not just a default (3)
  const isAnswered = useCallback((i: BatchItem) => {
    const r = i.first_response
    return r === 'Yes' || r === 'yes' || r === '5' || r === 5 || r === '4' || r === 4
  }, [])
  const recommendedItems = useMemo(() => items.filter(i => i.is_recommended !== false || isAnswered(i)), [items, isAnswered])
  const otherItems = useMemo(() => items.filter(i => i.is_recommended === false && !isAnswered(i)), [items, isAnswered])
  const [showOthers, setShowOthers] = useState(false)

  const renderChip = (item: BatchItem) => {
    const isEditing = editingItemName === item.name
    // Chip-color contract (Compendium B25): value / confirmation / editing on SEPARATE channels.
    // value -> fill/color/icon; editing -> ring (not a fill swap). confirmed defaults true here —
    // BatchItem doesn't yet carry needs_confirmation; threading it lights up the F123
    // unconfirmed-default dimming (tracked follow-up). All colors are flip-per-theme tokens.
    const vis = chipVisualState(item.first_response, { editing: isEditing })
    const iconEl =
      vis.icon === 'check'
        ? <CheckCircleIcon sx={{ fontSize: '0.9rem', cursor: 'pointer' }} onClick={(e) => handleIconClick(e, item)} />
        : vis.icon === 'minus'
          ? <RemoveIcon sx={{ fontSize: '0.9rem' }} />
          : vis.icon === 'question'
            ? <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: '0.9rem' }} />
    const nonRecommended = item.is_recommended === false
    return (
      <Chip
        key={item.name}
        label={item.option_label}
        size="small"
        variant={vis.filled ? "filled" : "outlined"}
        color="default"
        data-response={item.first_response ?? ''}
        data-questionnaire-name={item.name}
        icon={iconEl}
        onClick={() => handleChipBodyClick(item)}
        disabled={disabled}
        sx={{
          fontSize: '0.7rem',
          height: '20px',
          cursor: disabled ? 'default' : 'pointer',
          opacity: vis.opacity * (nonRecommended ? 0.6 : 1),
          color: `var(${vis.textToken})`,
          '& .MuiChip-icon': { fontSize: '0.8rem', color: 'inherit' },
          // editing -> ring (ringToken); else value border. non-recommended stays dashed.
          borderColor: `var(${vis.ring ? vis.ringToken : vis.borderToken}) !important`,
          borderStyle: (vis.ring ? 'solid' : nonRecommended ? 'dashed' : vis.borderStyle) + ' !important',
          borderWidth: (vis.ring ? '2px' : '1px') + ' !important',
          fontWeight: vis.bold ? 'bold' : 'normal',
          // Override global dark mode .MuiChip-filled !important rule in App.tsx
          '&&.MuiChip-filled': vis.filled
            ? { backgroundColor: `var(${vis.fillToken}) !important`, color: `var(${vis.textToken}) !important` }
            : {},
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        mb: 0.75,
        p: 0.75,
        pl: 1,
        borderLeft: '3px solid var(--color-primary)',
        borderRadius: '4px',
        backgroundColor: 'var(--color-background-subtle)',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: '0.8rem',
          color: 'var(--color-text-primary)',
          mb: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
        }}
      >
        {groupLabel}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {!isExposureDependent && (() => {
          // Deny-all CONTROL (Compendium B25): applied = CALM muted fill, never red — a
          // fully-negative ROS shouldn't read as a wall of alerts. Tokens flip per theme.
          const denyVis = chipVisualState(null, { kind: 'deny_all', denyAllApplied: allDenied })
          return (
            <Chip
              label={allDenied ? "All No" : "No"}
              size="small"
              variant={allDenied ? "filled" : "outlined"}
              color="default"
              icon={allDenied
                ? <CancelIcon sx={{ fontSize: '0.8rem !important', color: 'inherit' }} />
                : undefined
              }
              onClick={() => !disabled && handleDenyAll()}
              disabled={disabled}
              sx={{
                fontSize: '0.7rem',
                height: '20px',
                cursor: disabled ? 'default' : 'pointer',
                color: `var(${denyVis.textToken})`,
                borderColor: `var(${denyVis.borderToken}) !important`,
                fontWeight: 'bold',
                '& .MuiChip-icon': { color: 'inherit' },
                // Override global dark mode .MuiChip-filled !important rule in App.tsx
                '&&.MuiChip-filled': allDenied
                  ? { backgroundColor: `var(${denyVis.fillToken}) !important`, color: `var(${denyVis.textToken}) !important` }
                  : {},
              }}
            />
          )
        })()}
        {recommendedItems.map(renderChip)}
        {otherItems.length > 0 && (
          <Chip
            label={showOthers ? `hide ${otherItems.length}` : `+${otherItems.length} more`}
            size="small"
            variant="outlined"
            onClick={() => setShowOthers(!showOthers)}
            sx={{
              fontSize: '0.65rem',
              height: '20px',
              cursor: 'pointer',
              borderStyle: 'dashed',
              opacity: 0.6,
            }}
          />
        )}
        {showOthers && otherItems.map(renderChip)}
      </Stack>
    </Box>
  )
}

export default BatchChipGroup
